import { faCheckCircle } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { type FC } from 'react';
import { Navigate, useLocation } from 'react-router';
import type { ApplyForNewAssignmentLocationState } from 'src/apis/assignment-service/types';
import { Card } from 'src/components/data-display/card';
import { Alert } from 'src/components/feedback/alert';
import { PageHeaderBackLink } from 'src/components/layout/page-header';
import { Spacer } from 'src/components/layout/spacer';
import { Link } from 'src/components/navigation/link';
import { Heading } from 'src/components/text/heading';
import { useReferrer } from 'src/hooks/use-referrer';
import {
  CustomDocumentView,
  DocumentView,
} from 'src/pages/operators/components/company-required-documents';

const SuccessfullyApplied: FC = () => {
  const referrer = useReferrer({
    label: 'Assignments',
    location: '/operators/assignments/overview',
  });

  const location = useLocation<ApplyForNewAssignmentLocationState | null>();

  // The fix when user migth using deep link to this page
  if (!location.state) {
    return <Navigate replace to="/operators/assignments/overview" />;
  }
  const { createdAssignmentResponse: data, selectedOperatorId } =
    location.state;
  const assignedUsers = data.userAssignmentRequestedInfo.filter(
    (user) => user.assignmentRequested
  );
  const usersWithAssignments = data.userAssignmentRequestedInfo.filter(
    (user) => !user.assignmentRequested
  );

  const requiredDocs = data.requiredDocuments;
  const additionalOrganisationDocuments =
    requiredDocs?.additionalOrganisationDocuments || [];
  const additionalUserDocuments = requiredDocs?.additionalUserDocuments || [];
  const hasOrganisationDocs =
    requiredDocs &&
    (requiredDocs.companyRegisterExtract ||
      requiredDocs.currentCompanyReport ||
      requiredDocs.mostRecentFullYearFinancialReport ||
      requiredDocs.mostRecentInterimFinancialReport ||
      requiredDocs.creditSolvencyCheck ||
      requiredDocs.poa ||
      requiredDocs.creditRating ||
      additionalOrganisationDocuments.length > 0);

  return (
    <div className="grid items-center gap-md">
      <PageHeaderBackLink referrer={referrer} />
      <Card data-testid="successfully-submitted-card">
        <div className="grid items-center gap-md">
          {assignedUsers.length > 0 ? (
            <>
              <Heading mode="title" size="md">
                <FontAwesomeIcon
                  className="text-foreground-support-success"
                  icon={faCheckCircle}
                />{' '}
                Your assignment request was successfully submitted.
              </Heading>
              <strong>
                The applications for the following users were successfully
                created:
              </strong>
              <ul data-testid="list-of-users">
                {assignedUsers.map((user) => (
                  <li key={user.userId}>{user.userName}</li>
                ))}
              </ul>

              {usersWithAssignments.length > 0 && (
                <Alert type="info">
                  {`The ${usersWithAssignments.length === 1 ? 'user' : 'users'} `}
                  {usersWithAssignments
                    .map((user) => user.userName)
                    .reduce((acc, name, index, array) => {
                      if (index === 0) return name;
                      if (index === array.length - 1)
                        return `${acc} and ${name}`;
                      return `${acc}, ${name}`;
                    }, '')}{' '}
                  already {usersWithAssignments.length === 1 ? 'has' : 'have'}{' '}
                  an assignment to this operator.
                </Alert>
              )}
            </>
          ) : (
            <strong data-testid="users-already-have-assignments">
              The selected users already have an assignment with the chosen
              operator. No new user assignments were created.
            </strong>
          )}

          {((data.organisationAssignmentRequested && hasOrganisationDocs) ||
            (assignedUsers.length > 0 &&
              additionalUserDocuments.length > 0)) && (
            <Alert type="info">
              In order to complete your application for an assignment, please
              send the following documents to the operator:
              {data.organisationAssignmentRequested && hasOrganisationDocs && (
                <>
                  <Spacer />
                  <p>
                    <strong>
                      Please send the following documents to the operator to
                      complete your application:
                    </strong>
                  </p>
                  <div
                    className="grid items-center gap-xs"
                    data-testid="documents-list"
                  >
                    {requiredDocs.companyRegisterExtract && (
                      <DocumentView
                        label="Company Register Extract"
                        document={requiredDocs.companyRegisterExtract}
                      />
                    )}
                    {requiredDocs.currentCompanyReport && (
                      <DocumentView
                        label="Current Company Report"
                        document={requiredDocs.currentCompanyReport}
                      />
                    )}
                    {requiredDocs.mostRecentFullYearFinancialReport && (
                      <DocumentView
                        label="Most Recent (Full-Year) Financial Report"
                        document={
                          requiredDocs.mostRecentFullYearFinancialReport
                        }
                      />
                    )}
                    {requiredDocs.mostRecentInterimFinancialReport && (
                      <DocumentView
                        label="Most Recent (Interim) Financial Report"
                        document={requiredDocs.mostRecentInterimFinancialReport}
                      />
                    )}
                    {requiredDocs.creditSolvencyCheck && (
                      <DocumentView
                        label="Credit/Solvency Check Document"
                        document={requiredDocs.creditSolvencyCheck}
                      />
                    )}
                    {requiredDocs.poa && (
                      <DocumentView
                        label="PRISMA Shipper Admin Power of Attorney"
                        document={requiredDocs.poa}
                      />
                    )}
                    {requiredDocs.creditRating && (
                      <DocumentView
                        label="Credit Rating Certification"
                        document={requiredDocs.creditRating}
                        agency={requiredDocs.creditRating.agency}
                      />
                    )}
                  </div>

                  {additionalOrganisationDocuments.length > 0 && (
                    <div
                      className="grid items-center gap-xs"
                      data-testid="custom-documents-list"
                    >
                      {additionalOrganisationDocuments.map(
                        (organisationDocument) => (
                          <CustomDocumentView
                            key={organisationDocument.name}
                            additionalDocument={organisationDocument}
                          />
                        )
                      )}
                    </div>
                  )}
                </>
              )}
              {assignedUsers.length > 0 &&
                additionalUserDocuments.length > 0 && (
                  <>
                    <Spacer />
                    <strong>
                      Please send the following documents to the operator, as
                      they are required for each user:
                    </strong>
                    <div
                      className="grid items-center gap-xs"
                      data-testid="user-documents-list"
                    >
                      {additionalUserDocuments.map((userDocument) => (
                        <CustomDocumentView
                          key={userDocument.name}
                          additionalDocument={userDocument}
                        />
                      ))}
                    </div>
                  </>
                )}
            </Alert>
          )}

          <p>
            You can find the state of the current assignemts and the contact
            details of the operator{' '}
            <Link
              to={`/dashboard/operators/assignments/${selectedOperatorId}/users/${data.userAssignmentRequestedInfo[0]!.userId}`}
            >
              here
            </Link>
            .
          </p>
          <Spacer />

          <div className="grid grid-flow-col items-center justify-end gap-md">
            <Link
              mode="button-primary"
              to="/operators/assignments/apply-for-assignment"
              state={{ selectedUsers: [] }}
            >
              Apply for New Assignment
            </Link>
            <Link
              mode="button-primary"
              to="/operators/assignments/apply-for-assignment"
              state={{
                selectedUsers: data.userAssignmentRequestedInfo.map((user) => ({
                  userId: user.userId,
                  userName: user.userName,
                  userEmail: user.userEmail,
                })),
              }}
            >
              Apply with Same Users
            </Link>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default SuccessfullyApplied;
